import { describeError } from '@/shared/api/error-catalog';
import { ApiError, isProblemDetails } from '@/shared/api/problem';
import { useLoadingStore } from '@/shared/stores/loading';
import { correlationId } from '@/shared/api/correlation-id';

/**
 * WEB-006 — the single HTTP client. Correlation IDs, the global loading indicator,
 * timeouts, error normalisation and session-expiry handling are solved here once, rather
 * than being re-improvised at every call site. An ESLint rule forbids importing `fetch`
 * anywhere else under `src/`.
 */

const BASE_URL = '/api/v1';
const DEFAULT_TIMEOUT_MS = 15_000;

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler = () => {};

/**
 * Registered by the router at start-up. Injected rather than imported to keep this module
 * free of a circular dependency on the router, which itself needs the client.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  timeoutMs?: number;
  /**
   * Suppresses the global loading indicator. Reserved for background polling, where a
   * spinner would flicker on screen without the user having asked for anything.
   */
  silent?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const loading = useLoadingStore();
  const { method = 'GET', body, query, signal, timeoutMs = DEFAULT_TIMEOUT_MS, silent } = options;

  if (!silent) {
    loading.start();
  }

  // API-010 — an explicit timeout. Without one, a hung backend leaves the interface
  // spinning indefinitely with no way out for the user.
  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => timeoutController.abort(), timeoutMs);
  const composedSignal = signal
    ? AbortSignal.any([signal, timeoutController.signal])
    : timeoutController.signal;

  try {
    // FormData carries its own multipart boundary, so it must be passed through
    // untouched and its content-type left for the browser to set.
    const isFormData = body instanceof FormData;

    const response = await fetch(`${BASE_URL}${path}${buildQueryString(query)}`, {
      method,
      headers: buildHeaders(body !== undefined && !isFormData),
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
      // The session cookie must travel with every request.
      credentials: 'same-origin',
      signal: composedSignal,
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const payload = await readPayload(response);

    if (!response.ok) {
      throw toApiError(response.status, payload);
    }

    return payload as T;
  } catch (error) {
    throw normalizeTransportError(error, timeoutController.signal.aborted);
  } finally {
    window.clearTimeout(timeoutId);
    if (!silent) {
      loading.finish();
    }
  }
}

/**
 * A file download, through the same client as everything else.
 *
 * It exists here rather than as a plain `<a href>` for three reasons the anchor cannot
 * cover: the global loading indicator (an eight-page payroll run is not instant), the
 * expired-session redirect, and a readable message when the server refuses. An anchor to a
 * 403 navigates the browser to a JSON error page.
 *
 * The blob is returned rather than saved here; deciding the filename belongs to the caller.
 */
export async function downloadFile(
  path: string,
  query?: RequestOptions['query'],
  timeoutMs = 60_000,
): Promise<Blob> {
  const loading = useLoadingStore();
  loading.start();

  // A payroll run for a large organisation renders slower than a JSON response, so the
  // default 15 seconds is not the right ceiling here.
  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => timeoutController.abort(), timeoutMs);

  try {
    const response = await fetch(`${BASE_URL}${path}${buildQueryString(query)}`, {
      method: 'GET',
      headers: { accept: 'application/pdf, application/problem+json', 'x-correlation-id': correlationId() },
      credentials: 'same-origin',
      signal: timeoutController.signal,
    });

    if (!response.ok) {
      // The failure body is a problem document, so it reads as JSON even here.
      throw toApiError(response.status, await readPayload(response));
    }

    return await response.blob();
  } catch (error) {
    throw normalizeTransportError(error, timeoutController.signal.aborted);
  } finally {
    window.clearTimeout(timeoutId);
    loading.finish();
  }
}

/**
 * Hands a blob to the browser as a download.
 *
 * The object URL is revoked immediately afterwards: it pins the whole file in memory until
 * it is, and a payroll run left behind on every click adds up.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  /**
   * A multipart POST for small uploads. Deliberately has no progress callback: a logo is
   * a single small file, and the global loading indicator already answers "is something
   * happening". Document versions use XHR instead, because a 40 MB PDF genuinely needs a
   * percentage.
   */
  postForm: <T>(path: string, body: FormData, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
};

function buildHeaders(hasBody: boolean): HeadersInit {
  const headers: Record<string, string> = {
    accept: 'application/json, application/problem+json',
    // API-013 — a client-generated correlation id, echoed by the server, so a user report
    // can be traced to the exact request in the logs.
    'x-correlation-id': correlationId(),
  };

  if (hasBody) {
    headers['content-type'] = 'application/json';
  }

  return headers;
}

function buildQueryString(
  query: Record<string, string | number | boolean | undefined | null> | undefined,
): string {
  if (!query) {
    return '';
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }

  const serialized = params.toString();
  return serialized.length > 0 ? `?${serialized}` : '';
}

async function readPayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';

  if (!contentType.includes('json')) {
    return null;
  }

  try {
    return await response.json();
  } catch {
    // A malformed body on an otherwise valid response is a server problem, not something
    // the caller can act on. Surfacing null lets the error path handle it uniformly.
    return null;
  }
}

function toApiError(status: number, payload: unknown): ApiError {
  const problem = isProblemDetails(payload) ? payload : null;
  const code = problem?.code ?? (status === 401 ? 'SESSION_EXPIRED' : 'INTERNAL_ERROR');
  const described = describeError(code, status);

  if (status === 401) {
    // FRONT-004 — session expiry is handled in one place. The requirement is explicit:
    // when the session lapses and an API call is made, the user returns to the login
    // screen rather than staring at a failure they cannot interpret.
    onUnauthorized();
  }

  const message = [described.message, described.hint].filter(Boolean).join(' ');

  return new ApiError(status, code, message, problem?.traceId ?? null, problem?.errors ?? []);
}

function normalizeTransportError(error: unknown, timedOut: boolean): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (timedOut) {
    const described = describeError('TIMEOUT');
    return new ApiError(0, 'TIMEOUT', `${described.message} ${described.hint ?? ''}`.trim(), null);
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    // Deliberate cancellation — a superseded search, or a component unmounting. Not a
    // failure the user should ever be told about.
    return new ApiError(0, 'ABORTED', '', null);
  }

  const described = describeError('NETWORK_ERROR');
  return new ApiError(
    0,
    'NETWORK_ERROR',
    `${described.message} ${described.hint ?? ''}`.trim(),
    null,
  );
}
